'use strict';

import data from '../data/data';

class MlbTbdTeamToLeagueMap {

    /**
     * @throws `Error` if called; this class cannot be instantiated.
     */
    constructor() {
        throw new Error('MlbTbdTeamToLeagueMap cannot be instantiated.');
    }

    /**
     * Passed the ID of a "TBD team" (pseudo-teams which exist in MLB data for postseason games until it is known
     * which actual teams will play in those games), returns one of these strings: "103" (AL); "104" (NL); or "000"
     * (the default, if the ID isn't recognized, or if it's ID 2710 or 2711 (the two world-series clubs before we know
     * which league will be home/away), or if it's simply invalid input.  These three possible return values are
     * expected to be used to build logo URLs; the reason for the padded "000" is that "000.svg" is the generic icon
     * depicting a baseball.
     *
     * Note that "000.svg" is found (as of 9/11/2017) in "/images/logos/team-cap-on-[dark/light]/", while "103.svg"
     * and "104.svg" are found elsewhere (currently as of 9/11/2017) only in "/images/logos/league-on-light/".
     *
     * Also see `getLeagueInfoFromTbdTeamId()` in this class, a lower-level method which may be cleaner to work with,
     * depending on usage.
     *
     * Helpful info is here: https://jira.mlbam.com/browse/SSSS-4943
     *
     * @param {number|string} teamId The ID of a "TBD team" such as 4617 ("NL Div. Winner #1").  May be a number, or a
     *      string representation of a number.
     * @return {string} One of "103", "104", or "000"
     * @see MlbTbdTeamToLeagueMap.getLeagueInfoFromTbdTeamId
     */
    static getLeagueIdFromTbdTeamId(teamId) {
        const leagueInfo = MlbTbdTeamToLeagueMap.getLeagueInfoFromTbdTeamId(teamId);
        return (leagueInfo.unknownLeague ? data.defaultIdStr : leagueInfo.leagueId);
    }

    /**
     * A lower-level but generally cleaner alternative to the `getLeagueIdFromTbdTeamId()` method in this class.
     * Passed a `teamId`
     * expected to be the ID of a "TBD team" (pseudo-team which exists in MLB data for postseason games until it is
     * known which actual active teams will play in those games), returns ab object with two
     * properties: `unknownLeague`, a boolean indicating whether this TBD team is associated with a league or not; and
     * `leagueId`, the ID (as a string) of the league if `unknownLeague` is true - otherwise this property should be
     * ignored.
     *
     * Helpful info is here: https://jira.mlbam.com/browse/SSSS-4943
     *
     * @param {number|string} teamId The ID of a "TBD team" such as 4617 ("NL Div. Winner #1").  May be a number, or a
     *      string representation of a number.
     * @return {{unknownLeague: boolean, leagueId: string}}
     * @see https://jira.mlbam.com/browse/SSSS-4943
     */
    static getLeagueInfoFromTbdTeamId(teamId) {
        let teamIdNum = parseInt('' + teamId);

        if (isNaN(teamIdNum)) {
            teamIdNum = 0;
        }
        let isRecognizedLeagueId = !!data.mappings[teamIdNum];
        let leagueIdStr = data.mappings[teamIdNum] || data.defaultIdStr;
        let isLeagueId = (leagueIdStr !== data.defaultIdStr);
        return {
            unknownLeague: !isLeagueId,
            isRecognizedLeagueId: isRecognizedLeagueId,
            leagueId: isLeagueId ? leagueIdStr : null
        };
    }

    /**
     * Returns true if this is a postseason "tbd team", false if it's anything else (an active team; a formerly active
     * team; one of the ASG teams; an xref team; bad input; etc.).
     * @param {number|string} teamId Numeric team ID, as a number or string.
     * @return {boolean}
     */
    static isTbdTeam(teamId) {
        const leagueInfo = MlbTbdTeamToLeagueMap.getLeagueInfoFromTbdTeamId(teamId);
        return !leagueInfo.unknownLeague || leagueInfo.isRecognizedLeagueId;
    }
}

export default MlbTbdTeamToLeagueMap;
